import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { setHeadingSize } from 'lib/sitecore/component-params';
import { getEnumValue } from 'lib/util/enum';
import { useIsNRC } from 'lib/util/site-configuration';
import { classnames } from 'tailwindcss-classnames';
import QuoteDown from './SvgQuotes/quote-down';
import QuoteUp from './SvgQuotes/quote-up';

export type PullQuoteProps = Sitecore.Override.ComponentBase &
  Components.General.PullQuote.Fields.PullQuote;

const fontStyle = `font-semibold`;

const PullQuote = ({ fields }: PullQuoteProps): JSX.Element => {
  const isNRCVal = useIsNRC();
  if (!fields) {
    return <MissingDataSource />;
  }
  type HeadingSizes = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
  const headingSize: HeadingSizes | undefined =
    getEnumValue<HeadingSizes>(fields.quoteFontSize) || undefined;
  const headingClasses = classnames(setHeadingSize(headingSize), fontStyle);
  const headingClassesNRC = classnames(
    setHeadingSize(headingSize),
    fontStyle,
    'leading-9',
    'before:absolute',
    'before:content-["“"]',
    'md:before:text-6xxl',
    'before:text-4xl',
    'before:left-[24px]',
    'before:top-0',
    'md:before:top-[-2px]',
    'before:leading-11',
    'md:before:leading-13',
    'before:h-[38px]',
    'before:w-[15px]',
    'md:before:h-[54px]',
    'md:before:w-[24px]',
    'before:text-black',
    'before:top-0',
    'md:before:left-[24px]',
    'before:left-[8px]'
  );

  const quoteContainer = classnames(
    'max-w-screen-2xl',
    'mx-auto',
    'w-full',
    'block',
    'relative',
    'px-17',
    'py-10',
    'md:px-24',
    'md:py-20',
    'overflow-hidden',
    'bg-blue-navy',
    'text-white',
    'text-center',
    'pull-quote'
  );
  const quoteContainerNRC = classnames(
    'max-w-centerContainer',
    'mx-auto',
    'w-full',
    'block',
    'relative',
    'py-10',
    'md:py-20',
    'overflow-hidden',
    'text-black',
    'pull-quote'
  );
  const middleText = classnames('text-m', 'leading-4', fontStyle);
  const middleTextNRC = classnames(
    'text-body',
    'leading-5',
    'uppercase',
    'font-bold',
    'mt-4',
    'tracking-[1.44px]'
  );
  const additionalText = classnames('pt-2', 'leading-3');

  const gradientHr = classnames(
    'w-36',
    'h-1',
    'mx-auto',
    'my-4',
    'border-0',
    'bg-gradient-to-r',
    'from-blue',
    'to-green'
  );
  const gradientHrNRC = classnames(
    'max-w-screen-2xl',
    'h-1',
    'mx-auto',
    'my-4',
    'border-t-8',
    'border-green'
  );

  return (
    <div data-component="authorable/general/pullquote">
      {isNRCVal ? (
        <>
          <div className={classnames(quoteContainerNRC)}>
            <hr className={classnames(gradientHrNRC)} />
            <div className="relative pl-10 pr-6 md:pl-16 md:pr-3">
              <Text className={headingClassesNRC} field={fields.quote} tag="p" />
              <Text
                className={classnames(middleTextNRC)}
                field={fields.attributionField1}
                tag="p"
              />
              <Text
                className={classnames(additionalText)}
                field={fields.attributionField2}
                tag="p"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={classnames(quoteContainer)}>
            <Text className={headingClasses} field={fields.quote} tag="p" />
            <hr className={classnames(gradientHr)} />
            <Text className={classnames(middleText)} field={fields.attributionField1} tag="p" />
            <Text className={classnames(additionalText)} field={fields.attributionField2} tag="p" />
            <QuoteUp />
            <QuoteDown />
          </div>
        </>
      )}
    </div>
  );
};

export default PullQuote;
